import React, { useEffect, useMemo } from 'react';
import { Container, Stack, Typography, Grid } from '@mui/material';
import { Box } from '@mui/system';
import PermContactCalendarIcon from '@mui/icons-material/PermContactCalendar';
import UpdateIcon from '@mui/icons-material/Update';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import StayCurrentPortraitIcon from '@mui/icons-material/StayCurrentPortrait';
import InterestsIcon from '@mui/icons-material/Interests';
import CampaignIcon from '@mui/icons-material/Campaign';
import SourceIcon from '@mui/icons-material/Source';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import DateRangeIcon from '@mui/icons-material/DateRange';

import { BsTelephone } from 'react-icons/bs';
import { FaRegUser } from 'react-icons/fa';
import { GiSwapBag, GiModernCity } from 'react-icons/gi';
import { HiOutlineMail } from 'react-icons/hi';

import { useDispatch } from 'react-redux';
import { fDate } from '../../utils/formatTime';
import { NOT_AVAILABLE } from '../Global/Constants';

// components
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import ViewCardComponent from '../../components/ViewCards';
import CustomTableComponent from '../../components/CustomTable';

// selectors
import useProspectSelector from '../../store/Slice/Prospect/ProspectSelector';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';

// actions
import { getProspectViewData } from '../../store/Slice/Prospect/ProspectSlice';

function ViewProspect() {
  const dispatch = useDispatch();

  const { ProspectViewData } = useProspectSelector();
  const { LoginData } = useLoginSelector();

  const leadId = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('prospectLeadId'));
    } catch {
      return null;
    }
  }, []);

  const applicationDetails = ProspectViewData?.applicationDetails;
  const loanRequirement = ProspectViewData?.loanRequirement;
  const leadSource = ProspectViewData?.leadSource;

  useEffect(() => {
    if (!LoginData?.companyId || !LoginData?.userId || !leadId) return;

    dispatch(
      getProspectViewData({
        companyId: LoginData.companyId,
        userId: LoginData.userId,
        leadId
      })
    );
  }, [dispatch, LoginData?.companyId, LoginData?.userId, leadId]);

  const applicationFields = useMemo(
    () => [
      {
        icon: <PermContactCalendarIcon className="viewLabelIcon" size={24} />,
        label: 'Prospect ID',
        value: ProspectViewData?.leadId
      },
      {
        icon: <SourceIcon className="viewLabelIcon" size={24} />,
        label: 'Campaign Source',
        value: leadSource?.leadSource
      },
      {
        icon: <CampaignIcon className="viewLabelIcon" size={24} />,
        label: 'Campaign Name',
        value: applicationDetails?.campaignName
      },
      {
        icon: <DateRangeIcon className="viewLabelIcon" size={24} />,
        label: 'Prospect Date',
        value: leadSource?.leadDate ? fDate(leadSource.leadDate) : null
      },
      {
        icon: <SupervisorAccountIcon className="viewLabelIcon" size={24} />,
        label: 'Prospect Count',
        value: applicationDetails?.prospectCount
      },
      {
        icon: <AssignmentIndIcon className="viewLabelIcon" size={24} />,
        label: 'Assigned On',
        value: applicationDetails?.assignedOn ? fDate(applicationDetails.assignedOn) : null
      },
      {
        icon: <StayCurrentPortraitIcon className="viewLabelIcon" size={24} />,
        label: applicationDetails?.lastStage ? 'Last Stage' : 'Current Stage',
        value: applicationDetails?.lastStage || applicationDetails?.currentStage
      },
      {
        icon: <UpdateIcon className="viewLabelIcon" size={24} />,
        label: 'Last Update Date',
        value: applicationDetails?.lastUpdatedOn ? fDate(applicationDetails.lastUpdatedOn) : null
      }
    ],
    [ProspectViewData?.leadId, leadSource, applicationDetails]
  );

  const loanFields = useMemo(
    () => [
      {
        icon: <FaRegUser className="viewLabelIcon" size={24} />,
        label: 'Name',
        value: loanRequirement?.customerName
      },
      {
        icon: <BsTelephone className="viewLabelIcon" size={24} />,
        label: 'Phone',
        value: loanRequirement?.mobile
      },
      {
        icon: <InterestsIcon className="viewLabelIcon" size={24} />,
        label: 'Loan Type',
        value: loanRequirement?.loanTypeName
      },
      {
        icon: <InterestsIcon className="viewLabelIcon" size={24} />,
        label: 'Employment Type',
        value: loanRequirement?.employmentType
      },
      {
        icon: <GiSwapBag className="viewLabelIcon" size={24} />,
        label: 'Loan Amount',
        value: loanRequirement?.loanAmountRequested
      },
      {
        icon: <GiModernCity className="viewLabelIcon" size={24} />,
        label: 'City',
        value: loanRequirement?.cityName
      },
      {
        icon: <HiOutlineMail className="viewLabelIcon" size={24} />,
        label: 'Email',
        value: loanRequirement?.emailAddress
      }
    ],
    [loanRequirement]
  );

  const callLogColumns = useMemo(
    () => [
      { label: 'Call Date', key: 'callDateTime', formatter: fDate },
      { label: 'Call Agent', key: 'callAgentName' },
      { label: 'Status', key: 'callStatus' },
      { label: 'Comments', key: 'callComments' }
    ],
    []
  );

  return (
    <Page title="Kpros by Kalolytic Solutions || Prospect">
      <Container maxWidth="xl">
        <Box pt={3} pb={3} mb={4}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            className="pageHeading"
          >
            <PageTitle info="View Prospect" />
            <Box>
              <BrudCrumbs />
            </Box>
          </Stack>

          <Box container textAlign="right">
            <Box className="statusBox">
              <Typography variant="body2">Current Status:</Typography>
              <Typography variant="subtitle2" className="disbursedText">
                {applicationDetails?.currentStatus
                  ? applicationDetails.currentStatus
                  : `${NOT_AVAILABLE}`}
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={3}>
            <ViewCardComponent title="Application Details" fields={applicationFields || []} />
            <ViewCardComponent title="Loan Requirement" fields={loanFields || []} />
          </Grid>

          <Typography variant="body1" className="headingLineUp headingLineUpOut">
            Call Log
          </Typography>

          <Box className="viewLeadCard cardTopMinusMargin viewCardPadding">
            <CustomTableComponent
              columns={callLogColumns || []}
              data={ProspectViewData?.callLog || []}
            />
          </Box>
        </Box>
      </Container>
    </Page>
  );
}

export default ViewProspect;
